import React, { useState, useEffect } from 'react';
import { MuiThemeProvider, createMuiTheme, makeStyles } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { SnackbarProvider } from 'notistack';
import 'graphiql/graphiql.min.css';
import Notification from '@components/notification';
import { LANGUAGE } from './constants';
import AppContext from './app-context';
import history from './history';
import { getRequest, postRequest } from './services/network-service';
import dialog, {
  GlobalLoadingProgress, GlobalLoginDialog,
} from './services/dialog-service';
import { globalVariables, applyGlobalStyles } from './global';
import { useActionControl } from './hooks';
import Routes from './routes';
import userService from './services/user-service';
import getDefaultTheme from './default-theme';
import { lazyLoadDependencies } from './global/exports';

const useStyles = makeStyles((theme) => ({
  notificationError: { backgroundColor: theme.palette.error[theme.palette.type] },
}));

const ERROR_MESSAGE_MAX_SNACK = 3;
const ERROR_MESSAGE_AUTO_HIDE_DURATION = 5000;

function App() {
  lazyLoadDependencies();
  applyGlobalStyles();

  const classes = useStyles();

  const defaultLanguage = localStorage.getItem(LANGUAGE)
    || (navigator.languages && navigator.languages[0])
    || navigator.language
    || navigator.userLanguage;
  const [site, setSite] = useState(null);
  const [language, setLanguage] = useState(defaultLanguage);
  const [paletteType, setPaletteType] = useState('light');
  const currentDefaultTheme = getDefaultTheme(paletteType);
  const [theme, setTheme] = useState(createMuiTheme(currentDefaultTheme));
  const contextValue = {
    paletteType,
    setPaletteType,
    language,
    setLanguage: (newLanguage) => {
      setLanguage(newLanguage);
      localStorage.setItem(LANGUAGE, newLanguage);
    },
    theme,
    setTheme,
    site,
    setSite: (value) => {
      globalVariables.currentSite = value;
      setSite(value);
    },
    navigate: (url) => {
      history.push(url);
    },
    request: postRequest,
    getRequest,
    useActionControl,
    dialog,
    globalTemplates: globalVariables.templates,
    userService,
  };

  const PageTitle = () => (site ? (
    <Helmet>
      <title>{site.name}</title>
    </Helmet>
  ) : null);

  useEffect(() => {
    try {
      const font = new FontFace('Material Icons', 'local(Material Icons), local(MaterialIcons-Regular), url(/fonts/materialIcons-regular.woff2) format("woff2"), url(/fonts/materialIcons-regular.woff) format("woff"), url(/fonts/materialIcons-regular.ttf) format("truetype")', {
        family: 'Material Icons',
        style: 'normal',
        weight: 400,
      });

      font.load().then((loadedFace) => {
        document.fonts.add(loadedFace);
        document.body.classList.add('material-icon-font-loaded');
      });
    } catch (e) {
      const link = document.createElement('link');
      link.type = 'text/css';
      link.rel = 'stylesheet';
      link.href = '/styles/material-icon-font.css';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (paletteType !== theme.palette.type) {
      const newMuiTheme = createMuiTheme(currentDefaultTheme);
      const newTheme = {
        ...newMuiTheme,
        palette: {
          ...newMuiTheme.palette,
          type: paletteType,
        },
      };
      setTheme(newTheme);
    }
  }, [currentDefaultTheme, paletteType, setTheme, theme]);

  if (theme.palette.type !== paletteType) {
    return (
      <MuiThemeProvider theme={theme}>
        <GlobalLoadingProgress />
      </MuiThemeProvider>
    );
  }

  return (
    <MuiThemeProvider theme={theme}>
      <AppContext.Provider value={{ ...contextValue }}>
        <SnackbarProvider
          maxSnack={ERROR_MESSAGE_MAX_SNACK}
          autoHideDuration={ERROR_MESSAGE_AUTO_HIDE_DURATION}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          classes={{
            variantError: classes.notificationError,
          }}
          preventDuplicate
        >
          <PageTitle />
          <Routes />
          <Notification />
        </SnackbarProvider>
      </AppContext.Provider>
      <GlobalLoadingProgress />
      <GlobalLoginDialog />
    </MuiThemeProvider>
  );
}

export default App;
